import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building, Camera, Loader2, Pencil, Plus, Users } from "lucide-react";
import {
  useCreateSubOrganization,
  useMainOrganizationsForPicker,
  useSubOrgContext,
  useSubOrganizations,
} from "@/hooks/use-sub-organizations";

const SubCustomers = () => {
  const ctx = useSubOrgContext();
  const [params, setParams] = useSearchParams();
  const { data: mainOrgs } = useMainOrganizationsForPicker(ctx.isSystemAdmin);
  const [parentId, setParentId] = useState<string | null>(params.get("org"));
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const create = useCreateSubOrganization();
  const navigate = useNavigate();

  useEffect(() => {
    if (!parentId && ctx.isMainOrgUser && ctx.ownOrganization) setParentId(ctx.ownOrganization.id);
  }, [ctx.isMainOrgUser, ctx.ownOrganization, parentId]);

  const { data: subOrgs = [], isLoading } = useSubOrganizations(parentId, showArchived);
  const parentName =
    mainOrgs?.find((o) => o.id === parentId)?.name ||
    (ctx.ownOrganization?.id === parentId ? ctx.ownOrganization?.name : undefined);

  if (ctx.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ctx.canView) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Sub-customers are managed by your main organisation.</p>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!parentId || !name.trim()) return;
    const created = await create.mutateAsync({ parentId, name: name.trim() });
    setName("");
    setCreateOpen(false);
    navigate(`/dashboard/sub-customers/${created.id}?tab=users`);
  };

  return (
    <SidebarProvider>
      <div className="customer-admin-scope min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <FixedPageHeader
            title="Customer Management"
            description="Organisations you manage, with their own users, cameras and showcase page"
            icon={Building}
            actions={
              ctx.canManage && parentId ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New sub-customer
                </Button>
              ) : undefined
            }
          />

          <div className="px-4 md:px-6 pb-6 pt-24 md:pt-[8.5rem] space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              {ctx.isSystemAdmin && (
                <div className="w-72">
                  <Select
                    value={parentId ?? undefined}
                    onValueChange={(v) => {
                      setParentId(v);
                      setParams({ org: v });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a main organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {(mainOrgs || []).map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
                <Label htmlFor="show-archived">Show archived</Label>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{parentName ? `${parentName} sub-customers` : "Sub-customers"}</CardTitle>
                <CardDescription>
                  {subOrgs.length} sub-customer{subOrgs.length !== 1 ? "s" : ""}
                  {!ctx.canManage && " · view only, ask your organisation admin to make changes"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!parentId ? (
                  <p className="text-muted-foreground">Choose a main organisation to see its sub-customers.</p>
                ) : isLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : subOrgs.length === 0 ? (
                  <p className="text-muted-foreground">No sub-customers yet.</p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {subOrgs.map((o) => (
                      <Card key={o.id} className="h-full">
                        <CardContent className="p-4 space-y-4">
                          <div className="flex gap-4 items-start">
                            {o.logo_url ? (
                              <img src={o.logo_url} alt="" className="h-12 w-12 rounded object-contain bg-muted" />
                            ) : (
                              <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                                <Building className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <Link to={`/dashboard/sub-customers/${o.id}?tab=profile`} className="font-semibold truncate hover:underline">
                                  {o.name}
                                </Link>
                                {o.archived_at && <Badge variant="secondary">Archived</Badge>}
                                {o.visibility === "public" && !o.archived_at && <Badge variant="outline">Showcase live</Badge>}
                              </div>
                              <div className="flex gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {o.users} users</span>
                                <span className="flex items-center gap-1"><Camera className="h-4 w-4" /> {o.cameras} cameras</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" asChild>
                              <Link to={`/dashboard/sub-customers/${o.id}?tab=users`}><Users className="h-4 w-4 mr-1" /> Users</Link>
                            </Button>
                            <Button size="sm" variant="outline" asChild>
                              <Link to={`/dashboard/sub-customers/${o.id}?tab=cameras`}><Camera className="h-4 w-4 mr-1" /> Cameras</Link>
                            </Button>
                            <Button size="sm" variant="outline" asChild>
                              <Link to={`/dashboard/sub-customers/${o.id}?tab=profile`}><Pencil className="h-4 w-4 mr-1" /> {ctx.canManage ? "Edit" : "View"}</Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="customer-admin-dialog">
          <DialogHeader>
            <DialogTitle>New sub-customer</DialogTitle>
            <DialogDescription>You can add their profile, users and cameras next.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="sub-name">Organisation name</Label>
            <Input id="sub-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!name.trim() || create.isPending}>
              {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

export default SubCustomers;
